import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID } from '@adornis/baseql/baseqlTypes.js';
import { Arg, Entity, Field, Mutation, Query } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { type BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { ChosenProduct } from '@adornis/payments/db/chosenProduct.js';
import { Payment } from '@adornis/payments/db/payments.js';
import { Price } from '@adornis/payments/db/prices.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import type Stripe from 'stripe';
import { bhBaseQlContext } from '../bh-context.js';
import { ParticipationState } from '../enums/enums.js';
import { RegistrationSuspendedError } from '../errors/RegistrationSuspendedError.js';
import { notifyNachrueckplatzErhalten } from '../notification/nachrueckplatz-erhalten.js';
import { createPayment } from '../payment.js';
import { getPluginForScope } from '../plugins/plugin.js';
import { isDefined } from '../util.js';
import { BHPayment } from './BHPayment.js';
import { Bundle } from './Bundle.js';
import { BundlePrice } from './BundlePrice.js';
import { Participation } from './Participation.js';
import { ParticipationEinzelberatung } from './ParticipationEinzelberatung.js';
import { UserStammdaten } from './stammdaten.js';
import { User } from './user.js';
import { Workshop } from './workshop.js';

@Entity()
export class RegistrationProductData extends AdornisEntity {
  static override _class = 'RegistrationProductData';

  @validate(nonOptional())
  @Field(type => String, { default: v => v ?? A.getGloballyUniqueID() })
  _id!: string;

  @validate(nonOptional())
  @Field(type => ID)
  productID!: ID;
}

@Entity()
export class RegistrationWorkshopData extends RegistrationProductData {
  static override _class = 'RegistrationWorkshopData';
}

@Entity()
export class RegistrationEinzelberatungData extends RegistrationProductData {
  static override _class = 'RegistrationEinzelberatungData';

  @validate(nonOptional())
  @Field(type => ID)
  timeslotID!: ID;
}

@Entity()
export class BookingResult extends AdornisEntity {
  static override _class = 'BookingResult';

  @Field(type => String)
  loginToken!: string;

  @Field(type => [String])
  participationIDs!: string[];

  @Field(type => Boolean)
  paymentRequired!: boolean;

  @Field(type => [String])
  paymentIDs?: string[];
}

@Entity()
export class RegistrationData extends AdornisEntity {
  static override _class = 'RegistrationData';

  @validate(nonOptional())
  @Field(type => String)
  institutionID!: string;

  @validate(nonOptional())
  @Field(type => [RegistrationProductData], { default: v => v ?? [] })
  productData!: RegistrationProductData[];

  @validate(nonOptional({ allowFalsy: true }))
  @Field(type => Boolean, { default: v => v ?? false })
  acceptedTerms!: boolean;

  @Field(type => UserStammdaten)
  userStammdaten: Maybe<UserStammdaten>;

  @Field(type => User)
  user: Maybe<User>;

  @Mutation(type => BookingResult)
  static moveUpFromWaitinglistIntoParticipating(
    @Arg('participationID', type => String) participationID: string,
    @Arg('paymentMethod', type => String) paymentMethod: Maybe<string>,
  ) {
    return async (gqlFields: BaseQLSelectionSet<BookingResult>): Promise<BookingResult> => {
      const participation = await getByID(
        Participation,
        participationID,
      )({
        _id: 1,
        state: 1,
        productID: 1,
        waitingListPlace: 1,
        currentPaymentID: 1,
        user: User.allFields,
        product: {
          institutionID: 1,
          leftCapacity: 1,
          hasPrice: 1,
          appointments: { start: 1, end: 1, location, meetingLink: { link: 1 } },
        },
      });
      if (!participation) throw new Error('Participation not found');
      if (!participation.product) throw new Error('Participation has no product');
      if (!participation.user) throw new Error('Participation has no user');

      if (participation.state !== ParticipationState.WAITING)
        throw new Error('unexpected Participationstate: ' + participation.state);

      if (!(participation.product instanceof Workshop))
        throw new Error('waitninglist are only supported for woekshops right now');

      const paymentRequired = participation.product.hasPrice;
      if (paymentRequired) {
        if (!paymentMethod) throw new Error('payment required but paymentmethod is undefined');
      }

      if (!bhBaseQlContext.ignoreCapacity && participation.product.leftCapacity === 0) {
        throw new Error('there are no free spots left');
      }

      participation.state = ParticipationState.IN;
      participation.waitingListPlace = null;
      await participation.save();

      // notify plugin
      const institutionID = participation.product.institutionID;
      const paymentPlugin = getPluginForScope('payment', institutionID);
      await paymentPlugin.afterEnrollment(participation._id);

      // prepare result
      const participationIDs = [participation._id];
      const result = new BookingResult({ loginToken: '', participationIDs, paymentRequired });

      if (participation.product.hasPrice) {
        if (!paymentMethod) throw new Error('unreachable');
        const institutionID = participation.product.institutionID;
        const paymentIDs = await createPayment(institutionID, [participation._id], paymentMethod);
        if (paymentIDs.length !== 1)
          throw new Error('expected 1 newly created Payment, but ' + paymentIDs.length + ' ids were returned');

        result.paymentIDs = paymentIDs;
      }

      await notifyNachrueckplatzErhalten(participation.product.institutionID, participation.user, [participation]);

      logger.info({ userID: participation.user._id }, 'moved user up from waitinglist to participant');

      return result;
    };
  }

  static async generatePaymentLinkForBooking(
    paymentID: string,
    paymentMethods: Stripe.Checkout.SessionCreateParams.PaymentMethodType[] | null = null,
  ) {
    const user = await CurrentUserInfo.getMyself<User>()({ _id: 1, suspentions: { institutionID: 1 } });

    const payment = await getByID(
      BHPayment,
      paymentID,
    )({
      ...Payment.allFields,
      _id: 1,
      participationIDs: 1,
      products: { product: { _id: 1 }, priceID: 1 },
      participations: {
        _id: 1,
        productID: 1,
        product: {
          _id: 1,
          institutionID: 1,
          isPaymentNeeded: 1,
          hasPrice: 1,
          prices: { _id: 1, ...Price.allFields },
        },
      },
    });
    if (!payment) throw new Error('payment not found ' + paymentID);

    const participations = payment.participations;
    if (!participations.length) return null;

    const productIDs = participations.map(it => it?.productID as Maybe<string>).filter(isDefined);
    const products = participations.map(it => it?.product).filter(isDefined);
    const bundles = await Bundle.getValidBundlesForProducts(productIDs)(Bundle.allFields);
    const selectedBundles = Bundle.getSelectedBundles(bundles, productIDs);

    if (products.length === 0) return null;
    const institutionID = products[0]!.institutionID;

    // handle suspensions for maybe already existing users
    if (!user) throw new Error('no user found');
    if (user.suspentions.map(suspension => suspension.institutionID).includes(institutionID))
      throw new RegistrationSuspendedError();

    const payProductsJSON = products
      .filter(product => product.isPaymentNeeded)
      .map(product => {
        const bundle = selectedBundles.find(bundle => bundle.productIDs.includes(product._id));
        const chosenProduct = payment.products.find(p => p.product._id === product._id);
        if (!chosenProduct) throw new Error('unreachable');
        const bundlePriceIndex = product.prices.findIndex(
          price => price instanceof BundlePrice && price.bundleID === bundle?._id,
        );
        const priceID = (() => {
          if (bundlePriceIndex !== -1) return product.prices[bundlePriceIndex]?._id;
          return chosenProduct.priceID;
        })();

        return {
          amount: 1,
          product,
          priceID,
        };
      });

    const stripeCustomerID = await User.ensureStripeCustomerForUser(user._id);

    if (!payProductsJSON.length) return null;
    const paymentLink = await payment.createPaymentLink({
      paymentMethods,
      products: payProductsJSON,
      mode: 'payment',
      createInvoice: true,
      savePaymentMethod: true,
      successURL: `/registration?${new URLSearchParams({
        step: 'paid',
        institution: institutionID,
      }).toString()}`,
      cancelURL: `/registration?${new URLSearchParams({
        step: 'paid',
        institution: institutionID,
        canceledPayment: '1',
      }).toString()}`,
      customerID: stripeCustomerID,
      automaticTax: true,
      collectTaxID: true,
      requireBillingAddress: true,
    });

    return paymentLink;
  }

  static async retryPayment(paymentID: string) {
    const paymentLink = await this._retryPayment(paymentID)();
    goTo(paymentLink);
  }

  @Mutation(type => String)
  private static _retryPayment(@Arg('paymentID', type => ID) paymentID: string) {
    return async () => {
      // TODO
      const payment = await BHPayment.getByID<BHPayment>(paymentID)({
        ...BHPayment.allFields,
        products: { ...ChosenProduct.allFields, product: { ...BasicProduct.allFields, institutionID: 1 } },
      });
      if (!payment) throw new Error('_retryPayment:no payment found');

      const payProductsJSON = payment.products;
      const institutionID = payment.products[0]?.product.institutionID;

      const stripeCustomerID = await User.ensureStripeCustomerForUser(payment.userReference.userID);

      return payment.createPaymentLink({
        paymentMethods: null,
        products: payProductsJSON,
        mode: 'payment',
        createInvoice: true,
        savePaymentMethod: true,
        successURL: `/registration?${new URLSearchParams({
          step: 'paid',
          // data: JSON.stringify(registrationData.toJSON()),
          institution: institutionID,
        }).toString()}`,
        cancelURL: `/registration?${new URLSearchParams({
          step: 'paid',
          // data: JSON.stringify(registrationData.toJSON()),
          institution: institutionID,
          canceledPayment: '1',
        }).toString()}`,
        customerID: stripeCustomerID,
        automaticTax: true,
        collectTaxID: true,
        requireBillingAddress: true,
      });
    };
  }

  @Query(type => RegistrationData)
  static getRegistrationDataFromPaymentID(@Arg('paymentID', type => ID) paymentID: string) {
    return async (gqlFields: BaseQLSelectionSet<RegistrationData>) => {
      const payment = await BHPayment.getByID<BHPayment>(paymentID)(BHPayment.allFields);
      if (!payment) throw new Error('getRegistrationDataFromPaymentID:no payment found');
      const user = await User.getByID<User>(payment.userReference.userID ?? 'NO_USER')(User.allFields);
      if (!user) throw new Error('getRegistrationDataFromPaymentID:no user found');
      const participations = await Participation.getByID<Participation>(payment.participationIDs)({
        _id: 1,
        _class: 1,
        userID: 1,
        state: 1,
        createdAt: 1,
        updatedAt: 1,
        productID: 1,
        user: {
          _id: 1,
          email: 1,
          firstName: 1,
          lastName: 1,
          title: 1,
        },
      });
      if (!participations.length) throw new Error('getRegistrationDataFromPaymentID:no participations found');

      const institutionID = participations[0]?.product?.institutionID;
      if (!institutionID) throw new Error('getRegistrationDataFromPaymentID:no institution ID found');

      const userStammdaten = await UserStammdaten.getByUserAndInstitution(
        user._id,
        institutionID,
      )(UserStammdaten.allFields);
      if (!userStammdaten) throw new Error('getRegistrationDataFromPaymentID:no user stammdaten found');

      return new RegistrationData({
        institutionID,
        productData: participations.map(participation => {
          if (participation instanceof ParticipationEinzelberatung)
            return new RegistrationEinzelberatungData({
              productID: participation.productID,
              timeslotID: participation.timeslotID,
            });
          return new RegistrationWorkshopData({ productID: participation.productID });
        }),
        acceptedTerms: true, // couldn't get here else
        userStammdaten,
        user,
      });
    };
  }
}
